import { decodeTokenPayload, isRestrictedSessionToken } from '@packages/auth';
import { UI_USAGE_EVENTS_MAX_BATCH, type UiUsageEvent } from '@packages/types';

export const UI_USAGE_FLUSH_DELAY_MS = 10_000;

/** Oldest events are dropped past this, so a tab that cannot flush never grows without bound. */
export const UI_USAGE_MAX_QUEUED = 100;

export interface UiUsageChannelDeps {
  /** Sends one batch of at most `UI_USAGE_EVENTS_MAX_BATCH` events under the current session token. */
  send: (accountId: string, events: UiUsageEvent[]) => Promise<unknown>;
  /** The session token the next request would carry. */
  readToken: () => string | null;
}

export interface UiUsageChannel {
  accept: (event: UiUsageEvent) => void;
  flush: () => void;
}

/** Whose session an event was recorded under. */
interface SessionStamp {
  sub: string;
  acc: string;
}

interface QueuedEvent {
  event: UiUsageEvent;
  stamp: SessionStamp;
}

/** The user and account of an unrestricted session token; null for anything else, undecodable tokens included. */
function sessionStampOf(token: string | null): SessionStamp | null {
  if (token === null || isRestrictedSessionToken(token)) return null;
  const payload = decodeTokenPayload(token);
  if (typeof payload?.sub !== 'string' || typeof payload.acc !== 'string') return null;
  return { sub: payload.sub, acc: payload.acc };
}

/**
 * The in-memory delivery queue for one tab, driven by the session token rather than by any
 * mounted component. Nothing is persisted (no cookies, no storage).
 *
 * An event is stamped with the token's user and account when accepted, and sent only if the
 * token at flush time is still unrestricted and carries the same two claims. So a restricted
 * (impersonation) token never carries events queued before it, and one user's events never go
 * out under another user's token, even after a switch in another tab.
 *
 * Delivery is best effort. A failed batch is dropped, never retried and never reported:
 * telemetry must not toast, sign anyone out or add load to a struggling backend.
 */
export function createUiUsageChannel(deps: UiUsageChannelDeps): UiUsageChannel {
  let queue: QueuedEvent[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  function flush(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    const pending = queue;
    queue = [];
    const current = sessionStampOf(deps.readToken());
    if (current === null) return;
    const sendable = pending
      .filter(({ stamp }) => stamp.sub === current.sub && stamp.acc === current.acc)
      .map(({ event }) => event);
    for (let start = 0; start < sendable.length; start += UI_USAGE_EVENTS_MAX_BATCH) {
      // Dropped on failure by design; see the function doc.
      deps.send(current.acc, sendable.slice(start, start + UI_USAGE_EVENTS_MAX_BATCH)).catch(() => undefined);
    }
  }

  function accept(event: UiUsageEvent): void {
    const stamp = sessionStampOf(deps.readToken());
    if (stamp === null) return;
    queue.push({ event, stamp });
    if (queue.length > UI_USAGE_MAX_QUEUED) queue.shift();
    timer ??= setTimeout(flush, UI_USAGE_FLUSH_DELAY_MS);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);

  return { accept, flush };
}
